import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Users, Loader2, Copy, Download, Mail } from "lucide-react";
import { api } from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export const WaitlistModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 0, entries: [] });
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr("");
    api.waitlistList()
      .then((d) => setData(d))
      .catch(() => setErr("Failed to load waitlist"))
      .finally(() => setLoading(false));
  }, [open]);

  const emails = data.entries.map((e) => e.email);

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(emails.join("\n")); } catch {}
  };

  const exportCsv = () => {
    const rows = [["email", "signed_up"], ...data.entries.map((e) => [e.email, e.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "ripp-waitlist.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0B1220] border-white/10 text-white" data-testid="waitlist-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <Users size={16} className="text-[#38BDF8]" /> Waitlist
            <span data-testid="waitlist-count" className="ml-1 rounded-sm bg-[#38BDF8]/15 px-2 py-0.5 font-mono text-[11px] text-[#38BDF8]">
              {data.count} signup{data.count === 1 ? "" : "s"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-[11px] text-white/40">
            Everyone who joined from the ripp.xyz landing page.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-2 flex items-center gap-2">
          <button data-testid="waitlist-copy" onClick={copyAll} disabled={!emails.length}
            className="flex items-center gap-1.5 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-white/70 transition-colors hover:bg-white/10 disabled:opacity-40">
            <Copy size={12} /> Copy emails
          </button>
          <button data-testid="waitlist-export" onClick={exportCsv} disabled={!emails.length}
            className="flex items-center gap-1.5 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-white/70 transition-colors hover:bg-white/10 disabled:opacity-40">
            <Download size={12} /> Export CSV
          </button>
        </div>

        <div className="max-h-[340px] overflow-y-auto thin-scroll rounded-sm border border-white/10">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 font-mono text-xs text-white/40">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : err ? (
            <div className="py-12 text-center font-mono text-xs text-red-400">{err}</div>
          ) : !data.entries.length ? (
            <div className="py-12 text-center font-mono text-xs text-white/40">No signups yet.</div>
          ) : (
            <table className="w-full">
              <tbody data-testid="waitlist-rows">
                {data.entries.map((e, i) => (
                  <tr key={e.email + i} className="border-b border-white/5 last:border-0">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 font-mono text-[13px] text-white">
                        <Mail size={12} className="text-white/30" /> {e.email}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-[11px] text-white/40">{fmtDate(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
